import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ArticlesProvider } from "./context/ArticlesContext";
import { FAQProvider } from "./context/FAQContext";
import { ReviewsProvider } from "./context/ReviewsContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import { AdsProvider } from "./context/AdsContext";
import { ProductsProvider } from "./context/ProductsContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CookieProvider, CookieSettingsButton } from "./components/cookies/CookieConsent";
import useAnalytics from "./hooks/useAnalytics";

// Pages
import HomePage from "./pages/HomePage";
import VeterinairesPage from "./pages/VeterinairesPage";
import ConseilsPage from "./pages/ConseilsPage";
import ConseilDetailPage from "./pages/ConseilDetailPage";
import EspacePatientPage from "./pages/EspacePatientPage";
import EspaceVeterinairePage from "./pages/EspaceVeterinairePage";
import RecherchePage from "./pages/RecherchePage";
import ContactPage from "./pages/ContactPage";
import NotFoundPage from "./pages/NotFoundPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";

// Auth
import AuthCallback from "./components/auth/AuthCallback";

// Legal Pages
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import CGUPage from "./pages/CGUPage";
import ConfidentialitePage from "./pages/ConfidentialitePage";
import RGPDPage from "./pages/RGPDPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboard from "./pages/admin/AdminDashboard";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Router component that handles OAuth callback synchronously
function AppRouter() {
  const location = useLocation();
  
  // Track page views
  useAnalytics();
  
  // Check URL fragment for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Main Pages */}
      <Route path="/" element={<HomePage />} />
      <Route path="/veterinaires" element={<VeterinairesPage />} />
      <Route path="/conseils" element={<ConseilsPage />} />
      <Route path="/conseils/:slug" element={<ConseilsPage />} />
      <Route path="/mon-espace" element={<EspacePatientPage />} />
      <Route path="/espace-patient" element={<EspacePatientPage />} />
      <Route path="/espace-veterinaire" element={<EspaceVeterinairePage />} />
      <Route path="/recherche" element={<RecherchePage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Legal Pages */}
      <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
      <Route path="/cgu" element={<CGUPage />} />
      <Route path="/confidentialite" element={<ConfidentialitePage />} />
      <Route path="/rgpd" element={<RGPDPage />} />
      
      {/* Password Reset */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      
      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  // Auto-initialize backend on app start (ensures admin & articles exist)
  React.useEffect(() => {
    const initBackend = async () => {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`);
      } catch (e) {
        console.debug('Health check:', e);
      }
    };
    initBackend();
  }, []);

  return (
    <ThemeProvider>
      <CookieProvider>
        <SiteSettingsProvider>
          <ArticlesProvider>
            <FAQProvider>
              <ReviewsProvider>
                <AdsProvider>
                  <ProductsProvider>
                    <div className="App bg-white dark:bg-gray-900 min-h-screen transition-colors">
                      <BrowserRouter>
                        <Toaster position="top-center" richColors />
                        <AppRouter />
                        
                        {/* Cookie settings button (floating) */}
                        <CookieSettingsButton />
                      </BrowserRouter>
                    </div>
                  </ProductsProvider>
                </AdsProvider>
              </ReviewsProvider>
            </FAQProvider>
          </ArticlesProvider>
        </SiteSettingsProvider>
      </CookieProvider>
    </ThemeProvider>
  );
}

export default App;
